import { useState } from 'react'
import { supabase } from '../lib/supabase'
import Logo from '../Logo'

const ASSISTANT_CODE = 'ASSIST-11'

function normalizeCodes(value) {
  return Array.from(
    new Set(
      value
        .split(/[\s,;]+/)
        .map(code => code.trim().toUpperCase())
        .filter(Boolean)
    )
  )
}

export default function Login() {
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [name, setName] = useState('')
  const [phone, setPhone] = useState('')
  const [studentCodesText, setStudentCodesText] = useState('')
  const [assistantCode, setAssistantCode] = useState('')
  const [accountType, setAccountType] = useState('student')
  const [isSignUp, setIsSignUp] = useState(false)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [foundStudents, setFoundStudents] = useState([])
  const [lookingUp, setLookingUp] = useState(false)

  async function lookupCodes(value, type = accountType) {
    const codes = normalizeCodes(value)

    setLookingUp(true)

    if (!codes.length) {
      setFoundStudents([])
      setLookingUp(false)
      return
    }

    const { data } = await supabase
      .from('students')
      .select(
        'id, name, student_code, grade'
      )
      .in('student_code', codes)

    const byCode = new Map(
      (data || []).map(s => [
        s.student_code.toUpperCase(),
        s
      ])
    )

    const ordered = codes
      .map(code => byCode.get(code))
      .filter(Boolean)

    setFoundStudents(
      type === 'student'
        ? ordered.slice(0, 1)
        : ordered
    )

    setLookingUp(false)
  }

  function handleCodesChange(value) {
    const upper = value.toUpperCase()

    setStudentCodesText(upper)

    lookupCodes(upper)
  }

  async function handleLogin(e) {
    e.preventDefault()

    setLoading(true)
    setError('')

    const { error } =
      await supabase.auth.signInWithPassword({
        email: email.trim().toLowerCase(),
        password
      })

    if (error) {
      setError(error.message)
    }

    setLoading(false)
  }

  async function handleSignUp(e) {
    e.preventDefault()

    setLoading(true)
    setError('')

    const cleanEmail =
      email.trim().toLowerCase()

    const cleanName =
      name.trim()

    const codes =
      normalizeCodes(studentCodesText)

    if (!cleanName) {
      setError('Enter your full name')
      setLoading(false)
      return
    }

    if (password.length < 6) {
      setError(
        'Password must be at least 6 characters'
      )
      setLoading(false)
      return
    }

    if (accountType === 'teacher') {
      setError(
        'Teacher accounts are managed by the admin. Please choose another account type.'
      )
      setLoading(false)
      return
    }

    if (
      accountType === 'assistant' &&
      assistantCode.trim() !== ASSISTANT_CODE
    ) {
      setError('Invalid assistant code')
      setLoading(false)
      return
    }

    if (
      accountType === 'student' ||
      accountType === 'parent'
    ) {
      if (!codes.length) {
        setError(
          accountType === 'student'
            ? 'Enter your student code'
            : "Enter at least one child's student code"
        )

        setLoading(false)
        return
      }

      if (
        !foundStudents.length ||
        foundStudents.length !== codes.length
      ) {
        const foundCodes =
          new Set(
            foundStudents.map(
              s => s.student_code.toUpperCase()
            )
          )

        const missing =
          codes.filter(
            code => !foundCodes.has(code)
          )

        setError(
          `Student code not found: ${missing.join(', ')}`
        )

        setLoading(false)
        return
      }
    }

    const {
      data: authData,
      error: authError
    } =
      await supabase.auth.signUp({
        email: cleanEmail,
        password
      })

    if (authError) {
      setError(authError.message)
      setLoading(false)
      return
    }

    if (!authData.user) {
      setError('Could not create account')
      setLoading(false)
      return
    }

    const uid =
      authData.user.id

    let insertError = null

    if (accountType === 'assistant') {
      const { error: e } =
        await supabase
          .from('teachers')
          .insert({
            id: uid,
            name: cleanName,
            email: cleanEmail,
            phone: phone.trim(),
            role: 'assistant'
          })

      insertError = e

    } else if (accountType === 'parent') {
      const studentIds =
        foundStudents.map(
          s => s.id
        )

      const { error: e } =
        await supabase
          .from('parents')
          .insert({
            id: uid,
            name: cleanName,
            email: cleanEmail,
            phone: phone.trim(),

            // Keep the old field for compatibility.
            student_id: studentIds[0],

            // New field for multiple children.
            student_ids: studentIds
          })

      insertError = e

    } else if (accountType === 'student') {
      const { error: e } =
        await supabase
          .from('student_accounts')
          .insert({
            id: uid,
            name: cleanName,
            email: cleanEmail,
            phone: phone.trim(),
            student_id: foundStudents[0].id
          })

      insertError = e
    }

    if (insertError) {
      setError(
        'Error: ' + insertError.message
      )

      await supabase.auth.signOut()

      setLoading(false)
      return
    }

    await supabase.auth.signOut()

    setIsSignUp(false)
    setPassword('')
    setName('')
    setPhone('')
    setStudentCodesText('')
    setAssistantCode('')
    setFoundStudents([])

    setError(
      'Account created successfully! Please sign in.'
    )

    setLoading(false)
  }

  const accountTypes = [
    {
      type: 'student',
      icon: '🎓',
      label: 'Student'
    },
    {
      type: 'parent',
      icon: '👨‍👩‍👦',
      label: 'Parent'
    },
    {
      type: 'assistant',
      icon: '🧑‍💼',
      label: 'Assistant'
    },
  ]

  return (
    <div style={{
      minHeight: '100vh',
      minHeight: '-webkit-fill-available',
      background: 'var(--bg)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: 20
    }}>
      <div style={{
        width: '100%',
        maxWidth: 400,
        display: 'flex',
        flexDirection: 'column',
        gap: 14
      }}>

        {/* Teacher Info */}
        <div
          className="card"
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 14
          }}
        >
          <span style={{ fontSize: 34 }}>
            👩‍🏫
          </span>

          <div>
            <div style={{
              fontWeight: 600,
              fontSize: 15
            }}>
              Mrs. Aya Abdelfattah
            </div>

            <a
              href="tel:01121852785"
              style={{
                color: 'var(--accent)',
                fontSize: 13,
                textDecoration: 'none',
                display: 'flex',
                alignItems: 'center',
                gap: 4,
                marginTop: 3
              }}
            >
              📞 01121852785
            </a>
          </div>
        </div>

        {/* Login Card */}
        <div className="card">

          {/* Logo */}
          <div style={{
            textAlign: 'center',
            marginBottom: 22
          }}>
            <div style={{
              display: 'flex',
              justifyContent: 'center',
              marginBottom: 10
            }}>
              <Logo size={52} />
            </div>

            <div style={{
              fontSize: 22,
              fontWeight: 700,
              letterSpacing: -.3
            }}>
              A
              <span style={{
                fontSize: 14,
                fontWeight: 400,
                color: 'var(--accent)'
              }}>
                +
              </span>
              {' '}in Math
            </div>

            <div style={{
              fontSize: 13,
              color: 'var(--text3)',
              marginTop: 4
            }}>
              {isSignUp
                ? 'Create a new account'
                : 'Sign in to continue'}
            </div>
          </div>

          {/* Error */}
          {error && (
            <div style={{
              background:
                error.includes('successfully')
                  ? 'var(--success-bg)'
                  : 'var(--danger-bg)',

              color:
                error.includes('successfully')
                  ? 'var(--success)'
                  : 'var(--danger)',

              padding: '10px 14px',
              borderRadius: 8,
              fontSize: 13,
              marginBottom: 14,
              lineHeight: 1.5
            }}>
              {error}
            </div>
          )}

          <form
            onSubmit={
              isSignUp
                ? handleSignUp
                : handleLogin
            }
          >

            {isSignUp && (
              <>

                {/* Account Type */}
                <div className="field">
                  <label>Account Type</label>

                  <div style={{
                    display: 'grid',
                    gridTemplateColumns:
                      'repeat(3,1fr)',
                    gap: 8
                  }}>
                    {accountTypes.map(
                      ({
                        type,
                        icon,
                        label
                      }) => (
                        <button
                          key={type}
                          type="button"
                          onClick={() => {
                            setAccountType(type)
                            setFoundStudents([])
                            setStudentCodesText('')
                            setError('')
                          }}
                          style={{
                            padding: '10px 6px',
                            borderRadius: 10,
                            border:
                              '0.5px solid',
                            cursor: 'pointer',
                            transition:
                              'all .15s',

                            borderColor:
                              accountType === type
                                ? 'var(--accent)'
                                : 'var(--border)',

                            background:
                              accountType === type
                                ? 'var(--accent-bg)'
                                : 'var(--surface)',

                            color:
                              accountType === type
                                ? 'var(--accent)'
                                : 'var(--text3)',

                            fontWeight:
                              accountType === type
                                ? 600
                                : 400,

                            display: 'flex',
                            alignItems:
                              'center',
                            justifyContent:
                              'center',
                            flexDirection:
                              'column',
                            gap: 4,
                            fontSize: 12
                          }}
                        >
                          <span style={{
                            fontSize: 22
                          }}>
                            {icon}
                          </span>

                          {label}
                        </button>
                      )
                    )}
                  </div>
                </div>

                <div className="field">
                  <label>
                    Full Name
                  </label>

                  <input
                    value={name}
                    onChange={e =>
                      setName(e.target.value)
                    }
                    placeholder="Your full name"
                    required
                  />
                </div>

                <div className="field">
                  <label>
                    Phone Number
                  </label>

                  <input
                    value={phone}
                    onChange={e =>
                      setPhone(e.target.value)
                    }
                    placeholder="01xxxxxxxxx"
                    type="tel"
                  />
                </div>

                {/* Student / Parent */}
                {(accountType === 'student' ||
                  accountType === 'parent') && (
                  <div className="field">

                    <label>
                      {accountType === 'student'
                        ? 'Your Student Code'
                        : "Children's Student Codes"}
                    </label>

                    {accountType === 'parent' ? (
                      <textarea
                        value={studentCodesText}
                        onChange={e =>
                          handleCodesChange(
                            e.target.value
                          )
                        }
                        placeholder={
                          'Enter multiple codes, for example:\nAPM-101, APM-205, APM-318'
                        }
                        rows={3}
                        style={{
                          textTransform:
                            'uppercase',
                          letterSpacing: 1,
                          resize: 'vertical'
                        }}
                      />
                    ) : (
                      <input
                        value={studentCodesText}
                        onChange={e =>
                          handleCodesChange(
                            e.target.value
                          )
                        }
                        placeholder="e.g. APM-042"
                        style={{
                          textTransform:
                            'uppercase',
                          letterSpacing: 1
                        }}
                      />
                    )}

                    {accountType === 'parent' && (
                      <div style={{
                        fontSize: 11,
                        color: 'var(--text3)',
                        marginTop: 5
                      }}>
                        Separate codes with commas,
                        spaces, or new lines.
                      </div>
                    )}

                    {lookingUp && (
                      <div style={{
                        fontSize: 12,
                        color: 'var(--text3)',
                        marginTop: 6
                      }}>
                        🔍 Looking up...
                      </div>
                    )}

                    {foundStudents.length > 0 &&
                      !lookingUp && (
                        <div style={{
                          marginTop: 8,
                          display: 'flex',
                          flexDirection: 'column',
                          gap: 6
                        }}>
                          {foundStudents.map(s => (
                            <div
                              key={s.id}
                              style={{
                                background:
                                  'var(--success-bg)',
                                border:
                                  '0.5px solid var(--success)',
                                borderRadius: 10,
                                padding:
                                  '8px 12px',
                                display: 'flex',
                                alignItems:
                                  'center',
                                gap: 10
                              }}
                            >
                              <div style={{
                                width: 34,
                                height: 34,
                                borderRadius: '50%',
                                background:
                                  'var(--success)',
                                color: '#fff',
                                display: 'flex',
                                alignItems:
                                  'center',
                                justifyContent:
                                  'center',
                                fontWeight: 700,
                                fontSize: 15,
                                flexShrink: 0
                              }}>
                                {s.name
                                  .charAt(0)
                                  .toUpperCase()}
                              </div>

                              <div style={{
                                minWidth: 0
                              }}>
                                <div style={{
                                  fontWeight: 600,
                                  color:
                                    'var(--success)',
                                  fontSize: 12
                                }}>
                                  ✓ {s.student_code}
                                </div>

                                <div style={{
                                  fontSize: 13,
                                  color:
                                    'var(--text2)'
                                }}>
                                  {s.name}
                                </div>

                                {s.grade && (
                                  <div style={{
                                    fontSize: 11,
                                    color:
                                      'var(--text3)'
                                  }}>
                                    📚 {s.grade}
                                  </div>
                                )}
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                  </div>
                )}

                {accountType === 'assistant' && (
                  <div className="field">
                    <label>
                      Assistant Code
                    </label>

                    <input
                      value={assistantCode}
                      onChange={e =>
                        setAssistantCode(
                          e.target.value
                        )
                      }
                      placeholder="Enter assistant code"
                    />
                  </div>
                )}
              </>
            )}

            <div className="field">
              <label>
                Email Address
              </label>

              <input
                value={email}
                onChange={e =>
                  setEmail(e.target.value)
                }
                type="email"
                placeholder="you@email.com"
                required
              />
            </div>

            <div className="field">
              <label>Password</label>

              <input
                value={password}
                onChange={e =>
                  setPassword(e.target.value)
                }
                type="password"
                placeholder={
                  isSignUp
                    ? 'Minimum 6 characters'
                    : 'Your password'
                }
                required
              />
            </div>

            <button
              className="btn btn-primary"
              type="submit"
              disabled={loading}
              style={{
                width: '100%',
                minHeight: 44
              }}
            >
              {loading
                ? 'Please wait...'
                : isSignUp
                  ? 'Create Account'
                  : 'Sign In'}
            </button>
          </form>

          <div style={{
            textAlign: 'center',
            marginTop: 16
          }}>
            <button
              type="button"
              onClick={() => {
                setIsSignUp(v => !v)
                setError('')
                setFoundStudents([])
                setStudentCodesText('')
              }}
              style={{
                background: 'none',
                border: 'none',
                color: 'var(--accent)',
                cursor: 'pointer',
                fontSize: 13
              }}
            >
              {isSignUp
                ? 'Already have an account? Sign in'
                : 'Need an account? Sign up'}
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}